import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const EASE = 'cubic-bezier(.16,1,.3,1)';

function useInView({ threshold = 0.15, rootMargin = '0px 0px -10% 0px' } = {}) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current || inView) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold, rootMargin }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [inView, threshold, rootMargin]);
  return [ref, inView] as const;
}

const ProjectSection: React.FC<{ children: React.ReactNode; delay?: number; className?: string }> = ({
  children,
  delay = 0,
  className = '',
}) => {
  const [ref, inView] = useInView();
  return (
    <section
      ref={ref}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 900ms ${EASE} ${delay}ms, transform 900ms ${EASE} ${delay}ms`,
        willChange: 'opacity, transform',
      }}
    >
      {children}
    </section>
  );
};

const Projects: React.FC = () => {
  return (
    <div className="pt-24 md:pt-32 min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* AI Flashcards Section */}
        <ProjectSection className="mb-20" delay={0}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Image on Left */}
            <div className="order-2 lg:order-1">
              <div className="bg-[#D8F2FF] rounded-3xl p-8 flex items-center justify-center min-h-[400px]">
                <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-lg">
                  <div className="text-right text-sm text-gray-500 mb-4">20/30</div>
                  <div className="text-2xl font-bold text-gray-900 mb-8">What is Mitosis?</div>
                  <div className="flex justify-between">
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                      <span className="text-red-600 text-xl">✕</span>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                      <span className="text-green-600 text-xl">✓</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Text on Right */}
            <div className="order-1 lg:order-2">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">AI Flashcards</h2>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Self study app that allows students to utilise AI to memorise, learn and master any subject by creating study tools with AI and consulting an AI tutor.
              </p>
              <Link
                to="/projects/ai-flashcards"
                className="bg-[#D8F2FF] hover:bg-blue-300 text-black px-6 py-3 rounded-lg font-medium transition-colors duration-300 inline-flex items-center group"
              >
                See Case Study
                <span className="material-icons ml-2 text-lg transition-transform duration-300 group-hover:translate-x-1">
                  arrow_forward
                </span>
              </Link>
            </div>
          </div>
        </ProjectSection>

        {/* Glow Up Section */}
        <ProjectSection className="mb-20" delay={120}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Text on Left */}
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Glow Up</h2>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Habit tracker app that allows users to record and track daily habits of their choice, track sleep and visualise their progress.
              </p>
              <Link
                to="/projects/glow-up"
                className="bg-[#FFD5EA] hover:bg-pink-300 text-black px-6 py-3 rounded-lg font-medium transition-colors duration-300 inline-flex items-center group"
              >
                See Case Study
                <span className="material-icons ml-2 text-lg transition-transform duration-300 group-hover:translate-x-1">
                  arrow_forward
                </span>
              </Link>
            </div>
            {/* Image on Right */}
            <div>
              <div className="bg-pink-100 rounded-3xl p-8 flex items-center justify-center min-h-[400px]">
                <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-lg">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="text-sm font-medium text-gray-700 w-20">Reading</div>
                      <div className="flex gap-1 flex-1">
                        {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map((day, idx) => (
                          <div key={day} className={`w-8 h-8 rounded ${idx < 4 ? 'bg-pink-300' : 'bg-gray-200'}`}></div>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="text-sm font-medium text-gray-700 w-20">Walking</div>
                      <div className="flex gap-1 flex-1">
                        {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map((day, idx) => (
                          <div key={day} className={`w-8 h-8 rounded ${idx < 4 ? 'bg-pink-300' : 'bg-gray-200'}`}></div>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-medium text-gray-700 w-20">Vitamins</div>
                      <div className="flex gap-1 flex-1">
                        {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map((day, idx) => (
                          <div key={day} className={`w-8 h-8 rounded ${idx < 4 ? 'bg-purple-300' : 'bg-gray-200'}`}></div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ProjectSection>

        {/* Closetful Section */}
        <ProjectSection className="mb-20" delay={240}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Image on Left */}
            <div className="order-2 lg:order-1">
              <div className="bg-purple-100 rounded-3xl p-8 flex items-center justify-center min-h-[400px]">
                <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-lg">
                  <div className="text-sm font-medium text-gray-500 mb-4">Today&apos;s Look</div>
                  <div className="grid grid-cols-3 gap-2 mb-6">
                    {['Top', 'Pants', 'Shoes'].map((item) => (
                      <div key={item} className="aspect-square rounded-lg bg-purple-300/60 flex items-center justify-center">
                        <span className="text-xs text-gray-600">{item}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-lg font-semibold text-gray-900 mb-2">Casual Editorial</p>
                  <p className="text-sm text-gray-600">Styled from your closet</p>
                </div>
              </div>
            </div>
            {/* Text on Right */}
            <div className="order-1 lg:order-2">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Closetful</h2>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                An AI personal stylist that turns photos of the clothes you already own into editorial outfit suggestions — no shopping required.
              </p>
              <Link
                to="/projects/closetful"
                className="bg-purple-100 hover:bg-purple-300 text-black px-6 py-3 rounded-lg font-medium transition-colors duration-300 inline-flex items-center group"
              >
                See Case Study
                <span className="material-icons ml-2 text-lg transition-transform duration-300 group-hover:translate-x-1">
                  arrow_forward
                </span>
              </Link>
            </div>
          </div>
        </ProjectSection>
      </div>
      <footer className="section-padding">
        <div className="max-w-6xl mx-auto">
        </div>
      </footer>
    </div>
  );
};

export default Projects;
